import { Reader, Writer } from "protobufjs/minimal";
import type {
  SizeCharts_SizeSystem_Type,
  SizeCharts_BodyMeasurementsTable_BodyMeasurement_Type,
  SizeCharts_BodyMeasurementsTable_BodyMeasurement_Unit,
  SizeCharts,
  SizeCharts_SizeSystem,
  SizeCharts_ProductSizeTable,
  SizeCharts_ProductSizeTable_VendorSizeLabel,
  SizeCharts_BodyMeasurementsTable,
  SizeCharts_BodyMeasurementsTable_BodyMeasurement,
  SizeCharts_MeasurementHints,
  SizeCharts_MeasurementHints_InstructionPoint,
} from "../../aysa_api/models/size_charts.types";
import {
  createBaseImage,
  encodeImage,
  decodeImage,
} from "../../aysa_api/models/image";
import type { Long } from "protoc-gen-ts";

export const decodeSizeCharts_SizeSystem_Type = (
  reader: Reader
): SizeCharts_SizeSystem_Type => {
  switch (reader.int32()) {
    case 0:
      return 0;
    case 1:
      return 1;
    case 2:
      return 2;
    case 3:
      return 3;
    case 4:
      return 4;
    case 5:
      return 5;
    case 6:
      return 6;
    case 7:
      return 7;
    case 8:
      return 8;
    case 9:
      return 9;
    case 10:
      return 10;
    case 11:
      return 11;
    default:
      return 0;
  }
};

export const encodeSizeCharts_SizeSystem_Type = (
  writer: Writer,
  value: SizeCharts_SizeSystem_Type
): Writer => {
  return writer.int32(value);
};

export const decodeSizeCharts_BodyMeasurementsTable_BodyMeasurement_Type = (
  reader: Reader
): SizeCharts_BodyMeasurementsTable_BodyMeasurement_Type => {
  switch (reader.int32()) {
    case 0:
      return 0;
    case 1:
      return 1;
    case 2:
      return 2;
    case 3:
      return 3;
    case 4:
      return 4;
    default:
      return 0;
  }
};

export const encodeSizeCharts_BodyMeasurementsTable_BodyMeasurement_Type = (
  writer: Writer,
  value: SizeCharts_BodyMeasurementsTable_BodyMeasurement_Type
): Writer => {
  return writer.int32(value);
};

export const decodeSizeCharts_BodyMeasurementsTable_BodyMeasurement_Unit = (
  reader: Reader
): SizeCharts_BodyMeasurementsTable_BodyMeasurement_Unit => {
  switch (reader.int32()) {
    case 0:
      return 0;
    case 1:
      return 1;
    default:
      return 0;
  }
};

export const encodeSizeCharts_BodyMeasurementsTable_BodyMeasurement_Unit = (
  writer: Writer,
  value: SizeCharts_BodyMeasurementsTable_BodyMeasurement_Unit
): Writer => {
  return writer.int32(value);
};

export const createBaseSizeCharts = (): SizeCharts => {
  return {
    productSizeTable: createBaseSizeCharts_ProductSizeTable(),

    introductionText: "",
  };
};

export const decodeSizeCharts = (
  reader: Reader,
  length: number
): SizeCharts => {
  const end = reader.pos + length;
  const message = createBaseSizeCharts();

  while (reader.pos < end) {
    const tag = reader.uint32();
    switch (tag >>> 3) {
      case 1:
        message.productSizeTable = decodeSizeCharts_ProductSizeTable(
          reader,
          reader.uint32()
        );

        break;
      case 2:
        message.bodyMeasurementsTable = decodeSizeCharts_BodyMeasurementsTable(
          reader,
          reader.uint32()
        );

        break;
      case 3:
        message.measurementHints = decodeSizeCharts_MeasurementHints(
          reader,
          reader.uint32()
        );

        break;
      case 4:
        message.introductionText = reader.string();

        break;

      default:
        reader.skipType(tag & 7);
        break;
    }
  }

  return message;
};

export const encodeSizeCharts = (
  writer: Writer,
  message: SizeCharts
): Writer => {
  encodeSizeCharts_ProductSizeTable(
    writer.uint32(10).fork(),
    message.productSizeTable
  ).ldelim();
  if (message.bodyMeasurementsTable !== undefined) {
    encodeSizeCharts_BodyMeasurementsTable(
      writer.uint32(18).fork(),
      message.bodyMeasurementsTable
    ).ldelim();
  }
  if (message.measurementHints !== undefined) {
    encodeSizeCharts_MeasurementHints(
      writer.uint32(26).fork(),
      message.measurementHints
    ).ldelim();
  }
  writer.uint32(34).string(message.introductionText);

  return writer;
};

export const createBaseSizeCharts_SizeSystem = (): SizeCharts_SizeSystem => {
  return {
    type: 0,
    sizeLabels: [],
    name: "",
    shortName: "",
  };
};

export const decodeSizeCharts_SizeSystem = (
  reader: Reader,
  length: number
): SizeCharts_SizeSystem => {
  const end = reader.pos + length;
  const message = createBaseSizeCharts_SizeSystem();

  while (reader.pos < end) {
    const tag = reader.uint32();
    switch (tag >>> 3) {
      case 1:
        message.type = decodeSizeCharts_SizeSystem_Type(reader);

        break;
      case 2:
        message.sizeLabels.push(reader.string());

        break;
      case 3:
        message.name = reader.string();

        break;
      case 4:
        message.shortName = reader.string();

        break;

      default:
        reader.skipType(tag & 7);
        break;
    }
  }

  return message;
};

export const encodeSizeCharts_SizeSystem = (
  writer: Writer,
  message: SizeCharts_SizeSystem
): Writer => {
  encodeSizeCharts_SizeSystem_Type(writer.uint32(8), message.type);
  for (const value of message.sizeLabels) {
    writer.uint32(18).string(value);
  }
  writer.uint32(26).string(message.name);
  writer.uint32(34).string(message.shortName);

  return writer;
};

export const createBaseSizeCharts_ProductSizeTable =
  (): SizeCharts_ProductSizeTable => {
    return {
      vendorSizes: [],
      sizeSystems: [],
      headline: "",
      firstColumnHeader: "",
    };
  };

export const decodeSizeCharts_ProductSizeTable = (
  reader: Reader,
  length: number
): SizeCharts_ProductSizeTable => {
  const end = reader.pos + length;
  const message = createBaseSizeCharts_ProductSizeTable();

  while (reader.pos < end) {
    const tag = reader.uint32();
    switch (tag >>> 3) {
      case 1:
        message.vendorSizes.push(
          decodeSizeCharts_ProductSizeTable_VendorSizeLabel(
            reader,
            reader.uint32()
          )
        );

        break;
      case 2:
        message.sizeSystems.push(
          decodeSizeCharts_SizeSystem(reader, reader.uint32())
        );

        break;
      case 3:
        message.headline = reader.string();

        break;
      case 4:
        message.firstColumnHeader = reader.string();

        break;
      case 5:
        message.disclaimerText = reader.string();

        break;

      default:
        reader.skipType(tag & 7);
        break;
    }
  }

  return message;
};

export const encodeSizeCharts_ProductSizeTable = (
  writer: Writer,
  message: SizeCharts_ProductSizeTable
): Writer => {
  for (const value of message.vendorSizes) {
    encodeSizeCharts_ProductSizeTable_VendorSizeLabel(
      writer.uint32(10).fork(),
      value
    ).ldelim();
  }
  for (const value of message.sizeSystems) {
    encodeSizeCharts_SizeSystem(writer.uint32(18).fork(), value).ldelim();
  }
  writer.uint32(26).string(message.headline);
  writer.uint32(34).string(message.firstColumnHeader);
  if (message.disclaimerText !== undefined) {
    writer.uint32(42).string(message.disclaimerText);
  }

  return writer;
};

export const createBaseSizeCharts_ProductSizeTable_VendorSizeLabel =
  (): SizeCharts_ProductSizeTable_VendorSizeLabel => {
    return {
      label: "",
      sizeId: 0,
    };
  };

export const decodeSizeCharts_ProductSizeTable_VendorSizeLabel = (
  reader: Reader,
  length: number
): SizeCharts_ProductSizeTable_VendorSizeLabel => {
  const end = reader.pos + length;
  const message = createBaseSizeCharts_ProductSizeTable_VendorSizeLabel();

  while (reader.pos < end) {
    const tag = reader.uint32();
    switch (tag >>> 3) {
      case 1:
        message.label = reader.string();

        break;
      case 2:
        message.sizeId = (reader.int64() as Long).toNumber();

        break;

      default:
        reader.skipType(tag & 7);
        break;
    }
  }

  return message;
};

export const encodeSizeCharts_ProductSizeTable_VendorSizeLabel = (
  writer: Writer,
  message: SizeCharts_ProductSizeTable_VendorSizeLabel
): Writer => {
  writer.uint32(10).string(message.label);
  writer.uint32(16).int64(message.sizeId);

  return writer;
};

export const createBaseSizeCharts_BodyMeasurementsTable =
  (): SizeCharts_BodyMeasurementsTable => {
    return {
      sizeSystems: [],
      bodyMeasurements: [],
      headline: "",
    };
  };

export const decodeSizeCharts_BodyMeasurementsTable = (
  reader: Reader,
  length: number
): SizeCharts_BodyMeasurementsTable => {
  const end = reader.pos + length;
  const message = createBaseSizeCharts_BodyMeasurementsTable();

  while (reader.pos < end) {
    const tag = reader.uint32();
    switch (tag >>> 3) {
      case 1:
        message.sizeSystems.push(
          decodeSizeCharts_SizeSystem(reader, reader.uint32())
        );

        break;
      case 2:
        message.bodyMeasurements.push(
          decodeSizeCharts_BodyMeasurementsTable_BodyMeasurement(
            reader,
            reader.uint32()
          )
        );

        break;
      case 3:
        message.headline = reader.string();

        break;

      default:
        reader.skipType(tag & 7);
        break;
    }
  }

  return message;
};

export const encodeSizeCharts_BodyMeasurementsTable = (
  writer: Writer,
  message: SizeCharts_BodyMeasurementsTable
): Writer => {
  for (const value of message.sizeSystems) {
    encodeSizeCharts_SizeSystem(writer.uint32(10).fork(), value).ldelim();
  }
  for (const value of message.bodyMeasurements) {
    encodeSizeCharts_BodyMeasurementsTable_BodyMeasurement(
      writer.uint32(18).fork(),
      value
    ).ldelim();
  }
  writer.uint32(26).string(message.headline);

  return writer;
};

export const createBaseSizeCharts_BodyMeasurementsTable_BodyMeasurement =
  (): SizeCharts_BodyMeasurementsTable_BodyMeasurement => {
    return {
      type: 0,
      sizeLabels: [],
      unit: 0,
      name: "",
    };
  };

export const decodeSizeCharts_BodyMeasurementsTable_BodyMeasurement = (
  reader: Reader,
  length: number
): SizeCharts_BodyMeasurementsTable_BodyMeasurement => {
  const end = reader.pos + length;
  const message = createBaseSizeCharts_BodyMeasurementsTable_BodyMeasurement();

  while (reader.pos < end) {
    const tag = reader.uint32();
    switch (tag >>> 3) {
      case 1:
        message.type =
          decodeSizeCharts_BodyMeasurementsTable_BodyMeasurement_Type(reader);

        break;
      case 2:
        message.sizeLabels.push(reader.string());

        break;
      case 3:
        message.unit =
          decodeSizeCharts_BodyMeasurementsTable_BodyMeasurement_Unit(reader);

        break;
      case 4:
        message.name = reader.string();

        break;

      default:
        reader.skipType(tag & 7);
        break;
    }
  }

  return message;
};

export const encodeSizeCharts_BodyMeasurementsTable_BodyMeasurement = (
  writer: Writer,
  message: SizeCharts_BodyMeasurementsTable_BodyMeasurement
): Writer => {
  encodeSizeCharts_BodyMeasurementsTable_BodyMeasurement_Type(
    writer.uint32(8),
    message.type
  );
  for (const value of message.sizeLabels) {
    writer.uint32(18).string(value);
  }
  encodeSizeCharts_BodyMeasurementsTable_BodyMeasurement_Unit(
    writer.uint32(24),
    message.unit
  );
  writer.uint32(34).string(message.name);

  return writer;
};

export const createBaseSizeCharts_MeasurementHints =
  (): SizeCharts_MeasurementHints => {
    return {
      headline: "",
      image: createBaseImage(),
      instructionPoints: [],
    };
  };

export const decodeSizeCharts_MeasurementHints = (
  reader: Reader,
  length: number
): SizeCharts_MeasurementHints => {
  const end = reader.pos + length;
  const message = createBaseSizeCharts_MeasurementHints();

  while (reader.pos < end) {
    const tag = reader.uint32();
    switch (tag >>> 3) {
      case 1:
        message.headline = reader.string();

        break;
      case 2:
        message.image = decodeImage(reader, reader.uint32());

        break;
      case 3:
        message.instructionPoints.push(
          decodeSizeCharts_MeasurementHints_InstructionPoint(
            reader,
            reader.uint32()
          )
        );

        break;

      default:
        reader.skipType(tag & 7);
        break;
    }
  }

  return message;
};

export const encodeSizeCharts_MeasurementHints = (
  writer: Writer,
  message: SizeCharts_MeasurementHints
): Writer => {
  writer.uint32(10).string(message.headline);
  encodeImage(writer.uint32(18).fork(), message.image).ldelim();
  for (const value of message.instructionPoints) {
    encodeSizeCharts_MeasurementHints_InstructionPoint(
      writer.uint32(26).fork(),
      value
    ).ldelim();
  }

  return writer;
};

export const createBaseSizeCharts_MeasurementHints_InstructionPoint =
  (): SizeCharts_MeasurementHints_InstructionPoint => {
    return {
      headline: "",
      instruction: "",
    };
  };

export const decodeSizeCharts_MeasurementHints_InstructionPoint = (
  reader: Reader,
  length: number
): SizeCharts_MeasurementHints_InstructionPoint => {
  const end = reader.pos + length;
  const message = createBaseSizeCharts_MeasurementHints_InstructionPoint();

  while (reader.pos < end) {
    const tag = reader.uint32();
    switch (tag >>> 3) {
      case 2:
        message.headline = reader.string();

        break;
      case 3:
        message.instruction = reader.string();

        break;

      default:
        reader.skipType(tag & 7);
        break;
    }
  }

  return message;
};

export const encodeSizeCharts_MeasurementHints_InstructionPoint = (
  writer: Writer,
  message: SizeCharts_MeasurementHints_InstructionPoint
): Writer => {
  writer.uint32(18).string(message.headline);
  writer.uint32(26).string(message.instruction);

  return writer;
};
